import React, { lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import reportWebVitals from './reportWebVitals.js';
import { UserProvider } from './Contexts/LoginContext.js';
import { RoleProvider } from './Contexts/RoleContext.js';
import { EnquiryProvider } from './Contexts/EnquiryContext.js';
import { ThemeProvider as AppThemeProvider } from './Contexts/ThemeContext.jsx';
import MuiThemeWrapper from './theme/MuiThemeWrapper.jsx';
import { NotificationProvider } from './context/NotificationContext.jsx';
import { Provider } from "react-redux";
import { store } from "./store/store";
import { ToastContainer } from "react-toastify";
import CssBaseline from '@mui/material/CssBaseline';
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./static/theme.css";
import "./static/Sidebar.css";
import AppLayout from "./Components/AppLayout.jsx";
import AppointmentAttendanceToastWatcher from "./Components/AppointmentAttendanceToastWatcher.jsx";
import ProtectedRoute from './pages/ProtectedRoute.js';

// Eager: public / auth entry routes
import NotFound from './pages/NotFound.js';
import Login from './pages/Login.js';
import ForgotPassword from './pages/ForgotPassword.js';
import ResetPassword from './pages/ResetPassword.js';
import MfaVerify from './pages/MfaVerify.js';
import ExhibitionForm from './pages/ExhibitionForm.js';
import Unauthorized from './pages/Unauthorised.js';
import PublicBookingPage from "./pages/PublicBookingPage.jsx";

// Lazy: authenticated app pages (loaded via AppLayout Suspense)
const Dashboard = lazy(() => import('./pages/Dashboard.js'));
const CreateEnquiryWizard = lazy(() => import('./pages/CreateEnquiryWizard.jsx'));
const NewEnquiries = lazy(() => import('./pages/NewEnquiries.js'));
const EnquiryProfile = lazy(() => import('./pages/EnquiryProfile.js'));
const EditEnquiry = lazy(() => import('./pages/EditEnquiry.js'));
const ApplicationFromIssue = lazy(() => import('./pages/ApplciationFormIssue.js'));
const AllApplications = lazy(() => import('./pages/AllApplications.js'));
const CounsellorApplications = lazy(() => import('./pages/CounsellorApplications.js'));
const Users = lazy(() => import('./pages/Users.js'));
const MarketingOverview = lazy(() => import('./pages/MarketingOverview.js'));
const AllEnquiries = lazy(() => import('./pages/AllEnquiries.js'));
const UserProfile = lazy(() => import('./pages/UserProfile.js'));
const ExhibitionEnquriries = lazy(() => import('./pages/ExhibitionEnquiries.js'));
const CampaignAnalytics = lazy(() => import('./pages/CampaignAnalytics.js'));
const MediumOverview = lazy(() => import('./pages/MediumOverview.js'));
const VisaCounsellorAssignment = lazy(() => import('./pages/Assign_Visa_Counsellor.js'));
const MarketingAnalysis = lazy(() => import('./pages/MarketingAnalysis.js'));
const CounsellorAnalytics = lazy(() => import('./pages/CounsellorAnalysis.js'));
const LogsPage = lazy(() => import('./pages/Log.js'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage.jsx'));
const AppointmentsMyPage = lazy(() => import("./pages/AppointmentsMyPage.jsx"));
const AppointmentsAllPage = lazy(() => import("./pages/AppointmentsAllPage.jsx"));
const AppointmentsDashboardPage = lazy(() => import("./pages/AppointmentsDashboardPage.jsx"));
const AppointmentsCalendarPage = lazy(() => import("./pages/AppointmentsCalendarPage.jsx"));
const AppointmentsSettingsPage = lazy(() => import("./pages/AppointmentsSettingsPage.jsx"));
const UniversitiesListPage = lazy(() => import("./pages/UniversitiesListPage.jsx"));
const UniversitiesApprovalPage = lazy(() => import("./pages/UniversitiesApprovalPage.jsx"));
const UniversitiesReportsPage = lazy(() => import("./pages/UniversitiesReportsPage.jsx"));
const ReportBuilderPage = lazy(() => import("./pages/reports/ReportBuilderPage.jsx"));
const MonthlyTotalsReportPage = lazy(() => import("./pages/reports/MonthlyTotalsReportPage.jsx"));
const RegVisaAllocReportPage = lazy(() => import("./pages/reports/RegVisaAllocReportPage.jsx"));
const InquirySourceSummaryReportPage = lazy(() => import("./pages/reports/InquirySourceSummaryReportPage.jsx"));
const InstituteConversionReportPage = lazy(() => import("./pages/reports/InstituteConversionReportPage.jsx"));

const router = createBrowserRouter([
  {
    path: "/",
    element: <Login />,
    errorElement: <NotFound />
  },
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/reset-password", element: <ResetPassword /> },
  { path: "/mfa-verify", element: <MfaVerify /> },
  { path: "/exhibition-form", element: <ExhibitionForm /> },
  { path: "/unauthorized", element: <Unauthorized /> },
  // Temporarily disabled appointments
  // { path: "/book/:token", element: <PublicBookingPage /> },
  {
    element: <AppLayout />,
    children: [
      {
        path: "/exhibition-enquiries",
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Receptionist']}>
            <ExhibitionEnquriries />
          </ProtectedRoute>
        )
      },
      {
        path: "/home",
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Receptionist']}>
            <Dashboard />
          </ProtectedRoute>
        )
      },
      {
        path: "/create-enquiry",
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Receptionist']}>
            <CreateEnquiryWizard />
          </ProtectedRoute>
        )
      },
      {
        path: "/new-enquiry",
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Visa Counsellor', 'Counsellor', 'Visa&Application Counsellor']}>
            <NewEnquiries />
          </ProtectedRoute>
        )
      },
      {
        path: '/enquiry_profile',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor']}>
            <EnquiryProfile />
          </ProtectedRoute>
        ),
      },
      {
        path: '/edit_enquiry',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor']}>
            <EditEnquiry />
          </ProtectedRoute>
        ),
      },
      {
        path: '/marketing-overview',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <MarketingOverview />
          </ProtectedRoute>
        ),
      },
      {
        path: '/application-form-issue',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Receptionist', 'Manager']}>
            <ApplicationFromIssue />
          </ProtectedRoute>
        ),
      },
      {
        path: '/view-applications',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor', 'Manager']}>
            <AllApplications />
          </ProtectedRoute>
        ),
      },
      {
        path: '/all-enquiry',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Receptionist']}>
            <AllEnquiries />
          </ProtectedRoute>
        ),
      },
      {
        path: '/counsellor-applications',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa&Application Counsellor']}>
            <CounsellorApplications />
          </ProtectedRoute>
        )
      },
      {
        path: '/users',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <Users />
          </ProtectedRoute>
        )
      },
      {
        path: '/userProfile',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor']}>
            <UserProfile />
          </ProtectedRoute>
        )
      },
      {
        path: '/campaign_analytics',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <CampaignAnalytics />
          </ProtectedRoute>
        )
      },
      {
        path: '/medium-overview',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <MediumOverview />
          </ProtectedRoute>
        )
      },
      {
        path: '/assign-visa-counsellor',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Receptionist']}>
            <VisaCounsellorAssignment />
          </ProtectedRoute>
        )
      },
      {
        path: '/advertising-analysis',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <MarketingAnalysis />
          </ProtectedRoute>
        )
      },
      {
        path: '/marketing-counsellor-performance',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <CounsellorAnalytics />
          </ProtectedRoute>
        )
      },
      {
        path: '/server-logs',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <LogsPage />
          </ProtectedRoute>
        )
      },
      {
        path: '/reports/totals',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <MonthlyTotalsReportPage />
          </ProtectedRoute>
        )
      },
      {
        path: '/reports/reg-visa-alloc',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <RegVisaAllocReportPage />
          </ProtectedRoute>
        )
      },
      {
        path: '/reports/inquiry-source',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <InquirySourceSummaryReportPage />
          </ProtectedRoute>
        )
      },
      {
        path: '/reports/institute-conversion',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <InstituteConversionReportPage />
          </ProtectedRoute>
        )
      },
      {
        path: '/reports',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <ReportBuilderPage />
          </ProtectedRoute>
        )
      },
      {
        path: '/notifications',
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
            <NotificationsPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/universities/list",
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Manager']}>
            <UniversitiesListPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/universities/approval",
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Manager']}>
            <UniversitiesApprovalPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/universities/reports",
        element: (
          <ProtectedRoute allowedRoles={['admin', 'Manager']}>
            <UniversitiesReportsPage />
          </ProtectedRoute>
        )
      },
      // Temporarily disabled appointments
      // {
      //   path: "/appointments/my",
      //   element: (
      //     <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
      //       <AppointmentsMyPage />
      //     </ProtectedRoute>
      //   )
      // },
      // {
      //   path: "/appointments/all",
      //   element: (
      //     <ProtectedRoute allowedRoles={['admin', 'Manager']}>
      //       <AppointmentsAllPage />
      //     </ProtectedRoute>
      //   )
      // },
      // {
      //   path: "/appointments/dashboard",
      //   element: (
      //     <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
      //       <AppointmentsDashboardPage />
      //     </ProtectedRoute>
      //   )
      // },
      // {
      //   path: "/appointments/dashboard/:appointmentId",
      //   element: (
      //     <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
      //       <AppointmentsDashboardPage />
      //     </ProtectedRoute>
      //   )
      // },
      // {
      //   path: "/appointments/calendar",
      //   element: (
      //     <ProtectedRoute allowedRoles={['admin', 'Counsellor', 'Visa Counsellor', 'Receptionist', 'Visa&Application Counsellor', 'Manager']}>
      //       <AppointmentsCalendarPage />
      //     </ProtectedRoute>
      //   )
      // },
      // {
      //   path: "/appointments/settings",
      //   element: (
      //     <ProtectedRoute allowedRoles={['admin']}>
      //       <AppointmentsSettingsPage />
      //     </ProtectedRoute>
      //   )
      // },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <AppThemeProvider>
        <MuiThemeWrapper>
          <CssBaseline />
          <UserProvider>
            <RoleProvider>
              <NotificationProvider>
                <EnquiryProvider>
                  <>
                    <RouterProvider router={router} />
                    {/* Temporarily disabled appointments */}
                    {/* <AppointmentAttendanceToastWatcher /> */}
                    <ToastContainer position="top-right" autoClose={5000} newestOnTop />
                  </>
                </EnquiryProvider>
              </NotificationProvider>
            </RoleProvider>
          </UserProvider>
        </MuiThemeWrapper>
      </AppThemeProvider>
    </Provider>
  </React.StrictMode>
);

reportWebVitals();
